import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  CircleHelp,
  HeartPulse,
  RefreshCw,
  TriangleAlert,
  TrendingDown,
  TrendingUp,
  UserCheck,
  Users,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from "recharts";

import DashboardTabs from "../components/DashboardTabs";
import FiltersSheet from "../components/FiltersSheet";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  DataTable,
  DatePickerField,
  InfoHint,
  MultiSelectField,
  NumberFlowValue,
  PageLoader,
  SearchBar,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusBadge,
  Tabs,
} from "../components/ui";
import usePageFilters from "../hooks/usePageFilters";
import { getUsuarioLogado } from "../services/LoginService";
import { buscarReguaChurn } from "../services/churnService";
import { buscarRelatorioChurn } from "../services/relatorioService";
import { listarTodosUsuariosAtivos } from "../services/usuarioService";
import { formatIsoDateToDisplay, getTodayIso } from "../utils/dateFilters";
import { kpiGradientStyle } from "../utils/kpiCard";

const PAGE_SIZE_OPTIONS = [20, 50, 100];
// "Produzindo" para o card de leitura rápida: digitou ao menos uma vez nesta janela (dias corridos).
const PRODUZINDO_DIAS = 30;
const STATUS_META = {
  SAUDAVEL: { label: "Saudável", variant: "success", color: "hsl(var(--badge-success))" },
  ATENCAO: { label: "Atenção", variant: "attention", color: "hsl(var(--badge-attention))" },
  RISCO: { label: "Risco", variant: "warning", color: "hsl(var(--badge-warning))" },
  CRITICO: { label: "Crítico", variant: "critical", color: "hsl(var(--badge-critical))" },
  CHURN_CONSOLIDADO: { label: "Churn", variant: "churn", color: "hsl(var(--badge-churn))" },
  REATIVADO: { label: "Reativado", variant: "success", color: "var(--chart-2)" },
};
const STATUS_ORDER = ["SAUDAVEL", "REATIVADO", "ATENCAO", "RISCO", "CRITICO", "CHURN_CONSOLIDADO"];
// Ordem da tabela: primeiro quem ainda dá para recuperar, depois o churn, por fim os saudáveis.
const ACTION_RANK = { CRITICO: 0, RISCO: 1, ATENCAO: 2, CHURN_CONSOLIDADO: 3, REATIVADO: 4, SAUDAVEL: 5 };
const VIEWS = {
  acao: { label: "Precisam de ação", statuses: ["CRITICO", "RISCO", "ATENCAO"] },
  churn: { label: "Churn", statuses: ["CHURN_CONSOLIDADO"] },
  saudavel: { label: "Saudáveis", statuses: ["SAUDAVEL", "REATIVADO"] },
  todos: { label: "Todos", statuses: null },
};

function isAdminUser(user) {
  return String(user?.cargo || "").trim().toLowerCase() === "admin";
}

function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLocaleLowerCase("pt-BR")
    .trim();
}

function formatPercent(value, digits = 1) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  return `${Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;
}

function formatDecimal(value, digits = 1) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

// "3983 dias" não diz nada a quem lê; "há 10 anos" diz.
function formatTempoParado(dias) {
  if (dias === null || dias === undefined) return "Nunca digitou";
  if (dias === 0) return "Digitou hoje";
  if (dias < 60) return `Há ${dias} dia${dias === 1 ? "" : "s"}`;
  if (dias < 730) return `Há ${Math.floor(dias / 30)} meses`;
  return `Há ${Math.floor(dias / 365)} anos`;
}

function isProduzindo(partner) {
  const dias = partner?.diasSemDigitar;
  return dias !== null && dias !== undefined && dias < PRODUZINDO_DIAS;
}

function compareByAction(left, right) {
  const rank = (ACTION_RANK[left.status] ?? 9) - (ACTION_RANK[right.status] ?? 9);
  if (rank) return rank;
  const leftDays = left.diasSemDigitar ?? Number.MAX_SAFE_INTEGER;
  const rightDays = right.diasSemDigitar ?? Number.MAX_SAFE_INTEGER;
  if (leftDays !== rightDays) return leftDays - rightDays;
  return String(left.nome || "").localeCompare(String(right.nome || ""), "pt-BR");
}

function KpiTile({ icon: Icon, label, value, helper, accent, info, variation, variationUnit = "%" }) {
  const hasVariation = variation !== null && variation !== undefined && Number.isFinite(Number(variation));
  const variationValue = Number(variation || 0);
  const VariationIcon = variationValue > 0 ? TrendingUp : variationValue < 0 ? TrendingDown : Activity;

  return (
    <article
      className="relative min-w-0 overflow-hidden rounded-2xl text-kpi-foreground shadow-md"
      style={kpiGradientStyle(accent)}
    >
      <div className="relative flex items-start justify-between gap-3 p-5">
        <div className="min-w-0 space-y-1.5">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-kpi-foreground/90">
            <span>{label}</span>
            <InfoHint
              texto={info}
              rotulo={label}
              className="!border-kpi-foreground/60 !bg-kpi-foreground/15 !text-kpi-foreground hover:!bg-kpi-foreground/25 hover:!text-kpi-foreground"
            />
          </div>
          <p className="font-display text-[28px] font-semibold leading-tight tabular-nums">{value}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-kpi-foreground/85">
            {hasVariation ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-kpi-foreground/20 px-2 py-0.5 font-semibold text-kpi-foreground">
                <VariationIcon className="h-3 w-3" />
                {variationValue > 0 ? "+" : ""}{formatDecimal(variationValue)} {variationUnit}
              </span>
            ) : null}
            <span>{helper}</span>
          </div>
        </div>
        <Icon className="h-6 w-6 shrink-0 text-kpi-foreground/90" aria-hidden="true" />
      </div>
    </article>
  );
}

function Sparkline({ values = [] }) {
  const series = useMemo(
    () => (Array.isArray(values) ? values.map(Number).filter(Number.isFinite) : []),
    [values]
  );
  const points = useMemo(() => {
    if (!series.length) return "";
    const maximum = Math.max(...series, 1);
    return series
      .map((value, index) => {
        const x = series.length === 1 ? 50 : (index / (series.length - 1)) * 100;
        const y = 25 - (Math.max(0, value) / maximum) * 22;
        return `${x},${y}`;
      })
      .join(" ");
  }, [series]);

  // Linha reta no chão parece gráfico quebrado; sem nenhuma digitação, dizemos isso por extenso.
  if (!points || !series.some((value) => value > 0)) {
    return <span className="text-xs text-muted-foreground">Sem digitação</span>;
  }
  return (
    <svg viewBox="0 0 100 28" className="h-8 w-28 text-info" role="img" aria-label="Histórico diário de digitação">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function StatusTag({ status }) {
  const meta = STATUS_META[status] || { label: status || "Sem classificação", variant: "default" };
  return <StatusBadge status={meta.label} variant={meta.variant} shape="rounded" />;
}

function signalLabel(status) {
  return STATUS_META[status]?.label || "Sem leitura";
}

function ruleNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function ruleLabel(value) {
  return Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

function readRegua(regua = {}) {
  return {
    baselineDias: ruleNumber(regua.baselineDias, 56),
    cicloMinimoDias: ruleNumber(regua.cicloMinimoDias, 7),
    minimoDiasComMovimento: ruleNumber(regua.minimoDiasComMovimento, 3),
    entregaAtencao: ruleNumber(regua.entregaAtencao, 90),
    entregaRisco: ruleNumber(regua.entregaRisco, 80),
    entregaCritico: ruleNumber(regua.entregaCritico, 70),
    ciclosAtencao: ruleNumber(regua.ciclosAtencao, 1.5),
    ciclosRisco: ruleNumber(regua.ciclosRisco, 2),
    ciclosCritico: ruleNumber(regua.ciclosCritico, 3),
  };
}

// Mesmos cortes do backend, só para os exemplos do guia saírem coerentes com a régua atual.
function statusByEntrega(percent, rule) {
  if (percent >= rule.entregaAtencao) return "SAUDAVEL";
  if (percent >= rule.entregaRisco) return "ATENCAO";
  if (percent >= rule.entregaCritico) return "RISCO";
  return "CRITICO";
}

function statusByAtraso(vezes, rule) {
  if (vezes >= rule.ciclosCritico) return "CRITICO";
  if (vezes >= rule.ciclosRisco) return "RISCO";
  if (vezes >= rule.ciclosAtencao) return "ATENCAO";
  return "SAUDAVEL";
}

function GuideStep({ number, title, children }) {
  return (
    <section className="flex gap-3">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-muted font-display text-sm font-semibold text-foreground">
        {number}
      </span>
      <div className="min-w-0 flex-1 space-y-2">
        <h3 className="font-display text-base font-semibold text-foreground">{title}</h3>
        {children}
      </div>
    </section>
  );
}

function GuideExample({ children, status }) {
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-3 text-sm leading-relaxed text-muted-foreground">
      <p>{children}</p>
      {status ? (
        <div className="mt-2 flex items-center gap-2 text-xs">
          <span>Resultado:</span>
          <StatusTag status={status} />
        </div>
      ) : null}
    </div>
  );
}

function parseIsoDate(iso) {
  const [year, month, day] = String(iso || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatDayMonth(date) {
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function formatWeekday(date) {
  return date.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "");
}

// Faixa de dias mostrando a janela do ciclo terminando na data da base, e a mesma janela um dia depois.
function RollingWindowStrip({ referencia, cicloDias }) {
  const end = parseIsoDate(referencia);
  if (!end) return null;
  const firstDay = addDays(end, -cicloDias + 1);
  const days = Array.from({ length: cicloDias + 1 }, (_, index) => addDays(firstDay, index));
  const rows = [
    { label: "Hoje", start: 0 },
    { label: "Amanhã", start: 1 },
  ];

  return (
    <div className="overflow-x-auto rounded-xl border border-border p-3">
      <div className="grid min-w-[420px] gap-1.5" style={{ gridTemplateColumns: `64px repeat(${days.length}, minmax(0, 1fr))` }}>
        <span />
        {days.map((day) => (
          <span key={day.toISOString()} className="text-center text-[11px] leading-tight text-muted-foreground">
            <span className="block capitalize">{formatWeekday(day)}</span>
            <span className="block tabular-nums">{formatDayMonth(day)}</span>
          </span>
        ))}
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <span className="self-center text-xs font-semibold text-foreground">{row.label}</span>
            {days.map((day, index) => {
              const inside = index >= row.start && index < row.start + cicloDias;
              return (
                <span
                  key={day.toISOString()}
                  className={`h-6 rounded-md ${inside ? "bg-info/70" : "bg-muted"}`}
                  aria-hidden="true"
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function ChurnReadingGuide({ regua, referencia }) {
  const rule = readRegua(regua);
  const referenciaDate = parseIsoDate(referencia);
  const janelaInicio = referenciaDate ? addDays(referenciaDate, -rule.cicloMinimoDias + 1) : null;
  const exemploEntrega = 7;
  const exemploEsperado = 10;
  const exemploPercentual = (exemploEntrega / exemploEsperado) * 100;
  const exemploIntervalo = 3;
  const exemploParado = 7;
  const exemploVezes = exemploParado / exemploIntervalo;

  const statusGuide = [
    ["SAUDAVEL", `Entregou ${ruleLabel(rule.entregaAtencao)}% ou mais do esperado e está voltando a digitar no ritmo de sempre.`],
    ["ATENCAO", `Entregou entre ${ruleLabel(rule.entregaRisco)}% e ${ruleLabel(rule.entregaAtencao)}% do esperado, ou está parado há ${ruleLabel(rule.ciclosAtencao)} vezes o intervalo normal dele.`],
    ["RISCO", `Entregou entre ${ruleLabel(rule.entregaCritico)}% e ${ruleLabel(rule.entregaRisco)}% do esperado, ou está parado há ${ruleLabel(rule.ciclosRisco)} vezes o intervalo normal.`],
    ["CRITICO", `Entregou menos de ${ruleLabel(rule.entregaCritico)}% do esperado, ou está parado há ${ruleLabel(rule.ciclosCritico)} vezes o intervalo normal.`],
    ["CHURN_CONSOLIDADO", `Nunca digitou, digitou em menos de ${ruleLabel(rule.minimoDiasComMovimento)} dias em todo o histórico, ou ficou crítico por dois ciclos seguidos.`],
  ];

  return (
    <div className="space-y-7">
      <GuideStep number={1} title="O que é churn aqui">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Um parceiro entra em churn quando <strong className="text-foreground">deixa de produzir no ritmo dele</strong>.
          Cada parceiro é comparado <strong className="text-foreground">com ele mesmo</strong>, nunca com os outros:
          quem digita 2 contratos por semana não é cobrado como quem digita 50.
        </p>
      </GuideStep>

      <GuideStep number={2} title="Olhamos dois sinais">
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Volume</strong> — quanto ele digitou no ciclo atual, comparado ao que costuma digitar.
        </p>
        <GuideExample status={statusByEntrega(exemploPercentual, rule)}>
          Nas últimas semanas, o parceiro digitou em média {exemploEsperado} contratos por ciclo. Neste ciclo digitou{" "}
          {exemploEntrega}: <strong className="text-foreground">{ruleLabel(exemploPercentual)}% do esperado</strong>.
        </GuideExample>
        <p className="pt-1 text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Frequência</strong> — há quanto tempo ele não digita, comparado ao intervalo
          em que ele normalmente volta a digitar.
        </p>
        <GuideExample status={statusByAtraso(exemploVezes, rule)}>
          O parceiro costuma digitar a cada {exemploIntervalo} dias úteis. Está há {exemploParado} dias úteis sem digitar:{" "}
          <strong className="text-foreground">{ruleLabel(exemploVezes)} vezes o intervalo normal</strong>.
        </GuideExample>
      </GuideStep>

      <GuideStep number={3} title="A janela anda todo dia — não espera o domingo">
        <p className="text-sm leading-relaxed text-muted-foreground">
          O ciclo <strong className="text-foreground">não é a semana de segunda a domingo</strong>. É uma janela de{" "}
          {ruleLabel(rule.cicloMinimoDias)} dias que termina na data da base do relatório e anda um dia por vez.
          {referenciaDate && janelaInicio ? (
            <>
              {" "}Com a base em {formatWeekday(referenciaDate)} {formatDayMonth(referenciaDate)}, a janela vai de{" "}
              {formatWeekday(janelaInicio)} {formatDayMonth(janelaInicio)} até {formatWeekday(referenciaDate)}{" "}
              {formatDayMonth(referenciaDate)}. Amanhã, ela perde o primeiro dia e ganha o seguinte.
            </>
          ) : null}
        </p>
        <RollingWindowStrip referencia={referencia} cicloDias={rule.cicloMinimoDias} />
        <p className="text-sm leading-relaxed text-muted-foreground">
          Por isso o status é recalculado todo dia: quem parou de digitar numa segunda já aparece na terça, sem esperar a
          semana fechar. Parceiros que naturalmente digitam com menos frequência têm uma janela maior que{" "}
          {ruleLabel(rule.cicloMinimoDias)} dias, mas ela anda do mesmo jeito.
        </p>
        <GuideExample>
          O domingo só importa para a <strong className="text-foreground">taxa semanal</strong>: toda segunda de manhã o
          sistema guarda a taxa de churn do domingo anterior, para comparar uma semana com a outra. Os status e a tabela
          não dependem disso.
        </GuideExample>
      </GuideStep>

      <GuideStep number={4} title="Vale o pior dos dois">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Se o volume está saudável mas ele parou de digitar há muito tempo, o status segue a frequência — e vice-versa.
          Na tabela, a coluna <strong className="text-foreground">Sinal principal</strong> mostra qual dos dois pesou.
        </p>
      </GuideStep>

      <GuideStep number={5} title="Os status, com a régua atual">
        <div className="space-y-2">
          {statusGuide.map(([status, text]) => (
            <div key={status} className="flex items-start gap-3 rounded-xl border border-border p-3">
              <span className="w-24 shrink-0"><StatusTag status={status} /></span>
              <p className="text-sm leading-relaxed text-muted-foreground">{text}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Os percentuais vêm de Configurações › Régua de churn.</p>
      </GuideStep>

      <GuideStep number={6} title="Ativo não é o mesmo que produzindo">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Todo parceiro ativo no cadastro e na Join entra na conta, mesmo que esteja parado há anos — por isso a taxa de
          churn pode parecer alta. O card <strong className="text-foreground">Produzindo</strong> mostra quantos digitaram
          ao menos uma vez nos últimos {PRODUZINDO_DIAS} dias.
        </p>
      </GuideStep>

      <GuideStep number={7} title="Palavras da tela">
        <dl className="space-y-3 text-sm">
          {[
            ["Ciclo", `Janela móvel usada para medir o volume: termina na data da base e anda um dia por vez. Tem no mínimo ${ruleLabel(rule.cicloMinimoDias)} dias e fica maior para quem naturalmente digita com menos frequência.`],
            ["Esperado", `O que o parceiro costuma entregar em um ciclo, calculado com os últimos ${ruleLabel(rule.baselineDias)} dias dele.`],
            ["Dias na rotina", "Dias que contam para o atraso. Segunda a sexta sempre contam; sábado e domingo só se ele costuma digitar nesses dias."],
            ["Dias corridos", "Todos os dias do calendário desde a última digitação, inclusive fim de semana e feriado."],
          ].map(([term, text]) => (
            <div key={term}>
              <dt className="font-semibold text-foreground">{term}</dt>
              <dd className="mt-0.5 leading-relaxed text-muted-foreground">{text}</dd>
            </div>
          ))}
        </dl>
      </GuideStep>
    </div>
  );
}

function DistributionBar({ distribution, total, activeStatus, onSelect, criticalSources }) {
  if (!distribution.length || !total) {
    return <div className="py-6 text-center text-sm text-muted-foreground">Sem dados para distribuir.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex h-4 w-full overflow-hidden rounded-full bg-muted" role="img" aria-label="Distribuição da carteira por status">
        {distribution.map((item) => (
          <span
            key={item.status}
            className="h-full border-r-2 border-card last:border-r-0"
            style={{ width: `${(item.quantidade / total) * 100}%`, backgroundColor: item.fill }}
            title={`${item.name}: ${item.quantidade}`}
          />
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-3 xl:grid-cols-5">
        {distribution.map((item) => {
          const active = activeStatus === item.status;
          return (
            <button
              key={item.status}
              type="button"
              onClick={() => onSelect(item.status)}
              aria-pressed={active}
              className={`rounded-xl border px-3 py-2.5 text-left transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                active ? "border-foreground/40 bg-accent" : "border-border"
              }`}
            >
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.fill }} />
                {item.name}
              </span>
              <span className="mt-1 flex items-baseline gap-1.5">
                <span className="font-display text-xl font-semibold tabular-nums text-foreground">{item.quantidade}</span>
                <span className="text-xs tabular-nums text-muted-foreground">{formatPercent(item.percentual)}</span>
              </span>
              {item.status === "CRITICO" ? (
                <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                  {criticalSources.volume} volume · {criticalSources.frequencia} frequência · {criticalSources.ambos} ambos
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function RelatorioChurn() {
  const navigate = useNavigate();
  const todayIso = getTodayIso();
  const [usuario, setUsuario] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [regua, setRegua] = useState({});
  const [bootstrapping, setBootstrapping] = useState(true);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [referencia, setReferencia] = useState(todayIso);
  const [supervisorIds, setSupervisorIds] = useState([]);
  const [statusFilters, setStatusFilters] = useState([]);
  const [view, setView] = useState("acao");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const requestSequenceRef = useRef(0);
  const isAdmin = isAdminUser(usuario);

  useEffect(() => {
    let mounted = true;
    async function bootstrap() {
      try {
        const currentUser = await getUsuarioLogado();
        const [userList, currentRule] = await Promise.all([
          isAdminUser(currentUser)
            ? listarTodosUsuariosAtivos({ suppressActivityTracking: true }).catch(() => [])
            : Promise.resolve([]),
          buscarReguaChurn().catch(() => ({})),
        ]);
        if (!mounted) return;
        setUsuario(currentUser);
        setUsuarios(Array.isArray(userList) ? userList : []);
        setRegua(currentRule && typeof currentRule === "object" ? currentRule : {});
      } finally {
        if (mounted) setBootstrapping(false);
      }
    }
    bootstrap();
    return () => {
      mounted = false;
    };
  }, []);

  const supervisorOptions = useMemo(() => {
    const map = new Map();
    usuarios.forEach((user) => {
      String(user?.supervisorIds || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .forEach((id) => map.set(id, { value: id, label: user.nome }));
    });
    return Array.from(map.values()).sort((left, right) => left.label.localeCompare(right.label, "pt-BR"));
  }, [usuarios]);

  const request = useMemo(
    () => ({
      dataFim: referencia,
      supervisorId: isAdmin ? supervisorIds.map(Number).filter((id) => Number.isFinite(id) && id > 0) : [],
    }),
    [isAdmin, referencia, supervisorIds]
  );

  useEffect(() => {
    if (bootstrapping || !referencia) return;
    const sequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = sequence;
    setLoading(true);
    setError("");
    buscarRelatorioChurn(request)
      .then((data) => {
        if (requestSequenceRef.current === sequence) setReport(data);
      })
      .catch((requestError) => {
        if (requestSequenceRef.current !== sequence) return;
        setError(requestError?.response?.data?.message || "Não foi possível consultar o relatório de churn.");
      })
      .finally(() => {
        if (requestSequenceRef.current === sequence) setLoading(false);
      });
  }, [bootstrapping, referencia, request]);

  useEffect(() => setPage(0), [search, statusFilters, view, pageSize, report]);

  const parceiros = useMemo(() => (Array.isArray(report?.parceiros) ? report.parceiros : []), [report]);
  const produzindo = useMemo(() => parceiros.filter(isProduzindo).length, [parceiros]);
  const criticalSources = useMemo(() => {
    const sources = { volume: 0, frequencia: 0, ambos: 0 };
    parceiros.forEach((partner) => {
      if (partner.status !== "CRITICO") return;
      const volumeCritico = partner.statusQuantidade === "CRITICO";
      const frequenciaCritica = partner.statusFrequencia === "CRITICO";
      if (volumeCritico && frequenciaCritica) sources.ambos += 1;
      else if (frequenciaCritica) sources.frequencia += 1;
      else if (volumeCritico) sources.volume += 1;
    });
    return sources;
  }, [parceiros]);
  const viewCounts = useMemo(
    () => Object.fromEntries(Object.entries(VIEWS).map(([key, { statuses }]) => [
      key,
      statuses ? parceiros.filter((partner) => statuses.includes(partner.status)).length : parceiros.length,
    ])),
    [parceiros]
  );
  const normalizedSearch = normalizeSearchText(search);
  const filteredPartners = useMemo(() => {
    const viewStatuses = VIEWS[view]?.statuses;
    return parceiros
      .filter((partner) => {
        if (viewStatuses && !viewStatuses.includes(partner.status)) return false;
        if (statusFilters.length && !statusFilters.includes(partner.status)) return false;
        if (!normalizedSearch) return true;
        return normalizeSearchText(
          `${partner.nome} ${partner.cidade} ${partner.uf} ${partner.responsavel} ${partner.motivo}`
        ).includes(normalizedSearch);
      })
      .sort(compareByAction);
  }, [normalizedSearch, parceiros, statusFilters, view]);
  const totalPages = Math.max(1, Math.ceil(filteredPartners.length / pageSize));
  const pageRows = useMemo(
    () => filteredPartners.slice(page * pageSize, page * pageSize + pageSize),
    [filteredPartners, page, pageSize]
  );

  const distribution = useMemo(
    () => (Array.isArray(report?.resumo?.distribuicao) ? report.resumo.distribuicao : [])
      .filter((item) => Number(item?.quantidade || 0) > 0)
      .map((item) => ({
        ...item,
        quantidade: Number(item.quantidade || 0),
        name: STATUS_META[item.status]?.label || item.status,
        fill: STATUS_META[item.status]?.color || "hsl(var(--muted-foreground))",
      }))
      .sort((left, right) => STATUS_ORDER.indexOf(left.status) - STATUS_ORDER.indexOf(right.status)),
    [report]
  );
  const evolution = useMemo(
    () => (Array.isArray(report?.resumo?.evolucaoTaxa) ? report.resumo.evolucaoTaxa : []).map((point) => ({
      ...point,
      label: formatIsoDateToDisplay(point.semanaFim),
    })),
    [report]
  );

  const statusOptions = Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label }));
  usePageFilters({
    count: 1 + (isAdmin && supervisorIds.length ? 1 : 0) + (statusFilters.length ? 1 : 0),
    onOpen: () => setFiltersOpen(true),
  });

  // Clicar num status da distribuição leva a tabela até ele; clicar de novo desfaz.
  const selectDistributionStatus = (status) => {
    if (statusFilters.length === 1 && statusFilters[0] === status) {
      setStatusFilters([]);
      return;
    }
    const target = Object.entries(VIEWS).find(([, { statuses }]) => statuses?.includes(status));
    setView(target ? target[0] : "todos");
    setStatusFilters([status]);
  };

  const columns = useMemo(() => [
    {
      key: "nome",
      header: "Parceiro",
      width: 230,
      minWidth: 180,
      sortable: false,
      mobilePrimary: true,
      mobileOrder: 0,
      mobileSlot: "title",
      mobileRender: (_, row) => row.nome,
      render: (_, row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground">{row.nome}</p>
          <p className="truncate text-xs text-muted-foreground">{row.responsavel || "Sem responsável"}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: 120,
      sortable: false,
      mobileOrder: 1,
      mobileSlot: "badge",
      render: (value) => <StatusTag status={value} />,
    },
    {
      key: "diasSemDigitar",
      header: "Última digitação",
      width: 170,
      sortable: false,
      mobileOrder: 2,
      mobileSpan: 1,
      render: (value, row) => (
        <div className="tabular-nums">
          <p className="font-medium text-foreground">{formatTempoParado(value)}</p>
          {row.ultimoMovimento ? (
            <p className="text-xs text-muted-foreground">
              {formatIsoDateToDisplay(row.ultimoMovimento)}
              {value !== null && value < 60
                && row.diasOperacionaisSemDigitar !== null
                && row.diasOperacionaisSemDigitar !== undefined
                && row.diasOperacionaisSemDigitar !== value
                ? ` · ${row.diasOperacionaisSemDigitar} na rotina`
                : ""}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: "percentualEntrega",
      header: "Entrega no ciclo",
      width: 160,
      sortable: false,
      mobileOrder: 3,
      mobileSpan: 1,
      render: (_, row) => {
        const hasBase = row.esperadoNoCiclo !== null && row.esperadoNoCiclo !== undefined && Number(row.esperadoNoCiclo) > 0;
        if (!hasBase) return <span className="text-xs text-muted-foreground">Sem histórico para comparar</span>;
        return (
          <div className="tabular-nums">
            <p className="font-semibold text-foreground">{formatPercent(row.percentualEntrega, 0)}</p>
            <p className="text-xs text-muted-foreground">
              {row.realizadoNoCiclo ?? 0} de {formatDecimal(row.esperadoNoCiclo)} em {row.cicloDias || 0} dias
            </p>
          </div>
        );
      },
    },
    {
      key: "serie",
      header: "Últimos 56 dias",
      width: 140,
      sortable: false,
      mobileHidden: true,
      render: (value) => <Sparkline values={value} />,
    },
    {
      key: "motivo",
      header: "Sinal principal",
      width: 280,
      sortable: false,
      mobileOrder: 4,
      mobileSlot: "footer",
      mobileRender: (value, row) => (
        <div>
          <p className="text-sm text-foreground">{value || "—"}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{row.responsavel || "Sem responsável"}</p>
        </div>
      ),
      render: (value, row) => (
        <div>
          <p className="text-sm text-foreground">{value || "—"}</p>
          {row.statusQuantidade && row.statusFrequencia ? (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Volume: {signalLabel(row.statusQuantidade)} · Frequência: {signalLabel(row.statusFrequencia)}
            </p>
          ) : null}
        </div>
      ),
    },
  ], []);

  if (bootstrapping) return <PageLoader label="Carregando churn..." />;

  const resumo = report?.resumo || {};
  const total = Number(resumo.total || 0);
  const variation = resumo.variacaoTaxaPontosPercentuais;
  const crescimento = resumo.crescimentoEntregaPercentual;
  const hasCrescimento = crescimento !== null && crescimento !== undefined;
  const hasTrend = evolution.length >= 2;
  const refreshedAt = report?.atualizadoEm
    ? new Date(report.atualizadoEm).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
    : null;
  // A tela lê de tabela materializada pela varredura. Se ela parar de rodar, todo mundo "para de
  // digitar" e a carteira inteira cai em churn — o número fica errado sem nenhum sinal na tela.
  const diasDeAtraso = report?.referencia
    ? Math.round((parseIsoDate(todayIso) - parseIsoDate(report.referencia)) / 86400000)
    : 0;
  const baseDesatualizada = diasDeAtraso > 2;
  // Sem nenhuma digitação sincronizada, TODO MUNDO cai em churn e a tela anuncia 100% com ar de
  // verdade. Visto na homologação em 14/10: banco clonado sem as tabelas do churn, 593 parceiros,
  // 100,0%. Falta de dado tem que parecer falta de dado.
  const semSincronizacao = !report?.atualizadoEm;

  return (
    <div className="min-w-0 space-y-5">
      <FiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="Recorte da carteira e data usada para calcular a saúde de cada parceiro."
        onClear={() => {
          setReferencia(todayIso);
          setSupervisorIds([]);
          setStatusFilters([]);
        }}
        clearDisabled={referencia === todayIso && !supervisorIds.length && !statusFilters.length}
      >
        <div className="grid gap-5">
          <DatePickerField
            label="Data de referência"
            value={referencia}
            max={todayIso}
            onChange={(event) => setReferencia(event.target.value)}
          />
          {isAdmin ? (
            <MultiSelectField
              label="Supervisor"
              placeholder="Todos os supervisores"
              options={supervisorOptions}
              selectedValues={supervisorIds}
              onChange={setSupervisorIds}
            />
          ) : null}
          <MultiSelectField
            label="Status de saúde"
            placeholder="Todos os status"
            options={statusOptions}
            selectedValues={statusFilters}
            onChange={setStatusFilters}
          />
        </div>
      </FiltersSheet>

      <Sheet open={guideOpen} onOpenChange={setGuideOpen}>
        <SheetContent>
          <SheetHeader className="pr-16">
            <SheetTitle>Como ler este relatório</SheetTitle>
            <SheetDescription>Sete passos para entender o status de cada parceiro.</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
            <ChurnReadingGuide regua={regua} referencia={report?.referencia} />
          </div>
        </SheetContent>
      </Sheet>

      <DashboardTabs value="churn" />

      {error && !report ? (
        <Card>
          <div className="py-10 text-center text-sm text-destructive">{error}</div>
        </Card>
      ) : null}

      {!report && loading ? <PageLoader label="Calculando saúde da carteira..." /> : null}

      {report ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              Base de digitações até {formatIsoDateToDisplay(report.referencia)}
              {refreshedAt ? ` · dados atualizados em ${refreshedAt}` : ""}
              {loading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-label="Atualizando" /> : null}
            </p>
            <Button variant="outline" size="sm" onClick={() => setGuideOpen(true)}>
              <CircleHelp className="h-4 w-4" aria-hidden="true" />
              Como ler este relatório
            </Button>
          </div>

          {semSincronizacao ? (
            <Alert variant="warning">
              <TriangleAlert aria-hidden="true" />
              <div>
                <AlertTitle>Ainda não há digitações sincronizadas</AlertTitle>
                <AlertDescription>
                  Nenhuma digitação foi trazida da Join até agora, então não dá para medir a saúde da carteira: sem
                  histórico, todo parceiro aparece como churn. Os números abaixo só passam a valer depois da primeira
                  sincronização.
                </AlertDescription>
              </div>
            </Alert>
          ) : baseDesatualizada ? (
            <Alert variant="warning">
              <TriangleAlert aria-hidden="true" />
              <div>
                <AlertTitle>Os números abaixo estão desatualizados</AlertTitle>
                <AlertDescription>
                  A última sincronização com a Join trouxe dados até {formatIsoDateToDisplay(report.referencia)}, há{" "}
                  {diasDeAtraso} dias. Tudo o que foi digitado depois disso não está aqui, então a carteira aparece
                  parada e a taxa de churn fica alta demais. Os status voltam ao normal depois da próxima
                  sincronização.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}

          <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            <KpiTile
              icon={Users}
              label="Parceiros ativos"
              value={<NumberFlowValue value={total} format={{ maximumFractionDigits: 0 }} />}
              helper="Ativos no cadastro e na Join"
              accent="var(--chart-1)"
              info="Parceiros ativos com ID Join no cadastro. Todos entram na taxa de churn, inclusive os que estão parados há muito tempo."
            />
            <KpiTile
              icon={UserCheck}
              label="Produzindo"
              value={<NumberFlowValue value={produzindo} format={{ maximumFractionDigits: 0 }} />}
              helper={`digitaram nos últimos ${PRODUZINDO_DIAS} dias · ${Math.max(0, total - produzindo)} parados`}
              accent="hsl(var(--badge-success))"
              info={`Dos parceiros ativos, quantos digitaram pelo menos um contrato nos últimos ${PRODUZINDO_DIAS} dias. Ajuda a separar quem está ativo só no cadastro de quem está de fato produzindo.`}
            />
            <KpiTile
              icon={hasCrescimento && crescimento < 0 ? TrendingDown : TrendingUp}
              label="Entrega vs esperado"
              value={hasCrescimento
                ? <NumberFlowValue value={crescimento} suffix="%" format={{ minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: "exceptZero" }} />
                : "—"}
              helper={`${Number(resumo.realizadoNoCiclo || 0).toLocaleString("pt-BR")} digitadas de ${formatDecimal(resumo.esperadoNoCiclo, 0)} esperadas no ciclo`}
              accent={hasCrescimento && crescimento < 0 ? "hsl(var(--badge-danger))" : "var(--chart-2)"}
              info="Soma do que a carteira digitou no ciclo atual comparada com o que ela costuma digitar, respeitando o ritmo de cada parceiro. Negativo significa abaixo do normal."
            />
            <KpiTile
              icon={HeartPulse}
              label="Taxa de churn"
              value={<NumberFlowValue value={resumo.taxaChurnPercentual || 0} suffix="%" format={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }} />}
              helper={variation === null || variation === undefined
                ? "Tendência disponível após o 2º fechamento semanal"
                : "desde o fechamento anterior"}
              variation={variation}
              variationUnit="p.p."
              accent="var(--chart-5)"
              info="Parceiros em churn divididos por toda a carteira ativa. Nunca digitar, digitar pouco demais ou parar de digitar contam como churn."
            />
          </div>

          <Card>
            <div className="mb-4 flex items-center gap-2">
              <h2 className="font-display text-lg font-semibold text-foreground">Saúde da carteira</h2>
              <InfoHint texto="Status final de cada parceiro: entre queda de volume e quebra de frequência, prevalece o sinal mais grave. Clique em um status para ver os parceiros na tabela." rotulo="saúde da carteira" />
            </div>
            <DistributionBar
              distribution={distribution}
              total={distribution.reduce((sum, item) => sum + item.quantidade, 0)}
              activeStatus={statusFilters.length === 1 ? statusFilters[0] : null}
              onSelect={selectDistributionStatus}
              criticalSources={criticalSources}
            />
          </Card>

          <Card
            title="Parceiros"
            actions={[
              <SearchBar
                key="search"
                placeholder="Buscar parceiro ou responsável"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full sm:w-80"
              />,
            ]}
          >
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Tabs
                idPrefix="churn-view"
                ariaLabel="Grupo de parceiros"
                value={view}
                onChange={(next) => {
                  setView(next);
                  setStatusFilters([]);
                }}
                items={Object.entries(VIEWS).map(([value, { label }]) => ({
                  value,
                  label: `${label} (${viewCounts[value] || 0})`,
                }))}
              />
              {statusFilters.length ? (
                <button
                  type="button"
                  onClick={() => setStatusFilters([])}
                  className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  Limpar filtro de status ({statusFilters.map((status) => STATUS_META[status]?.label || status).join(", ")})
                </button>
              ) : null}
            </div>
            {error ? <p className="mb-3 text-sm text-destructive">{error}</p> : null}
            <DataTable
              columns={columns}
              data={pageRows}
              loading={loading && !report}
              rowKey={(row) => row.parceiroIdJoin}
              onRowClick={(row) => row.parceiroId && navigate(`/parceiros/${row.parceiroId}/vida-agente`)}
              emptyState="Nenhum parceiro encontrado com os filtros selecionados."
              pagination={{ page, size: pageSize, totalElements: filteredPartners.length, totalPages }}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </Card>

          {hasTrend ? (
            <Card>
              <div className="flex items-center gap-2">
                <h2 className="font-display text-lg font-semibold text-foreground">Taxa de churn semana a semana</h2>
                <InfoHint texto="Cada ponto é a taxa congelada no fechamento de um domingo. Mostra se a carteira está melhorando ou piorando ao longo do tempo; quanto mais baixa a linha, melhor." rotulo="evolução da taxa" />
              </div>
              <p className="mb-4 mt-1 text-sm text-muted-foreground">Quanto mais baixa a linha, melhor.</p>
              <ChartContainer config={{ taxa: { label: "Taxa de churn", color: "var(--chart-5)" } }} className="h-[220px]">
                <LineChart data={evolution} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                  <YAxis tickLine={false} axisLine={false} tickFormatter={(value) => `${value}%`} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                  <ChartTooltip
                    content={<ChartTooltipContent formatter={(value) => [formatPercent(value), "Taxa de churn"]} />}
                  />
                  <Line type="monotone" dataKey="taxaPercentual" name="Taxa de churn" stroke="var(--chart-5)" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ChartContainer>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
